import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Flag, Loader2, Star, CheckCircle2, X, Sparkles, Ticket, ShieldCheck, Megaphone, MessageSquare, Share2, RefreshCw, Award, AlertCircle, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminGame from "@/pages/AdminGame";
import AdminGamePlayers from "@/pages/AdminGamePlayers";
import AdminModeration from "@/pages/AdminModeration";
import AdminBanner from "@/pages/AdminBanner";
import AdminMessages from "@/pages/AdminMessages";
import AdminReferrals from "@/pages/AdminReferrals";
import DateNav from "@/components/DateNav";
import { useAuth } from "@/context/AuthContext";

const field = "w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]";

const TYPES_PARI = ["Simple gagnant", "Simple placé", "Simple Gagnant/Placé"];

const emptySel = () => ({ numero: "", cheval: "", etoiles: 3, commentaire: "" });
const todayISO = () => new Date().toISOString().slice(0, 10);

const heureToMin = (h) => {
  if (!h || typeof h !== "string" || !h.includes("h")) return 9999;
  const [hh, mm] = h.split("h");
  return (parseInt(hh, 10) || 0) * 60 + (parseInt(mm, 10) || 0);
};

function AntPronoAdmin() {
  const [form, setForm] = useState({
    date: todayISO(), hippodrome: "", reunion: "R1", course: "C1", discipline: "Plat", heure: "",
    moins_8_partants: false, type_pari: "", mon_ticket: "", commentaire: "", selections: [emptySel()],
  });
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [settleFor, setSettleFor] = useState(null);
  const [arrivee, setArrivee] = useState("");
  const [rapG, setRapG] = useState("");
  const [rapP, setRapP] = useState("");
  const [rapportFor, setRapportFor] = useState(null);

  const [mode, setMode] = useState("geny");
  const [genyUrl, setGenyUrl] = useState("");
  const [genyData, setGenyData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [genyType, setGenyType] = useState("");
  const [genyNb, setGenyNb] = useState("");
  const [genyStars, setGenyStars] = useState(3);
  const [genyTicket, setGenyTicket] = useState("");
  const [genyComment, setGenyComment] = useState("");

  const analyzeGeny = async () => {
    if (!genyUrl.trim()) return;
    setAnalyzing(true);
    setGenyData(null);
    try {
      const r = await api.post("/admin/geny-horse", { url: genyUrl.trim() });
      setGenyData(r.data);
      setGenyNb(r.data.nombre_partants || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  const publishGeny = async () => {
    if (!genyData?.horse) return;
    if (!TYPES_PARI.includes(genyType)) {
      toast.error("Choisissez un type de pari");
      return;
    }
    const nb = parseInt(genyNb) || genyData.nombre_partants || 0;
    setSaving(true);
    try {
      await api.post("/admin/courses", {
        date: genyData.date || todayISO(),
        hippodrome: genyData.hippodrome || "Geny",
        reunion: genyData.reunion || "R1",
        course: genyData.course || "C1",
        discipline: genyData.discipline || "",
        heure: genyData.heure || "",
        type_pari: genyType,
        mon_ticket: genyTicket,
        commentaire: genyComment,
        programme_url: genyData.programme_url || "",
        moins_8_partants: nb > 0 && nb < 8,
        selections: [{
          numero: genyData.horse.numero,
          cheval: genyData.horse.cheval,
          etoiles: genyStars,
          commentaire: genyData.horse.musique ? `Musique ${genyData.horse.musique}` : "",
          casaque: genyData.horse.casaque || "",
          jockey: genyData.horse.jockey || "",
        }],
      });
      toast.success("Pronostic publié depuis Geny !");
      setGenyUrl(""); setGenyData(null); setGenyType(""); setGenyNb(""); setGenyStars(3); setGenyTicket(""); setGenyComment("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const load = () => api.get("/courses", { params: { date: form.date } }).then((r) => setCourses(r.data.courses || r.data));
  useEffect(() => { load(); }, [form.date]); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSel = (i, k, v) => {
    const s = [...form.selections];
    s[i][k] = v;
    setForm({ ...form, selections: s });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        selections: form.selections
          .filter((s) => s.cheval && s.numero !== "")
          .map((s) => ({ ...s, numero: parseInt(s.numero), etoiles: parseInt(s.etoiles) })),
      };
      if (payload.selections.length === 0) {
        toast.error("Ajoutez au moins un cheval");
        setSaving(false);
        return;
      }
      if (!TYPES_PARI.includes(payload.type_pari)) {
        toast.error("Choisissez un type de pari");
        setSaving(false);
        return;
      }
      await api.post("/admin/courses", payload);
      toast.success("Pronostic publié !");
      setForm({ ...form, hippodrome: "", heure: "", moins_8_partants: false, type_pari: "", mon_ticket: "", commentaire: "", selections: [emptySel()] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/courses/${id}`);
    toast.success("Supprimé");
    load();
  };

  const settle = async () => {
    try {
      const nums = arrivee.split(/[\s,\-]+/).filter(Boolean).map((n) => parseInt(n));
      await api.post(`/admin/courses/${settleFor.id}/settle`, { arrivee: nums, rapport_gagnant: rapG || null, rapport_place: rapP || null });
      toast.success("Résultat enregistré et points attribués !");
      setSettleFor(null);
      setArrivee("");
      setRapG("");
      setRapP("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveRapports = async () => {
    try {
      await api.patch(`/admin/courses/${rapportFor.id}/rapports`, { rapport_gagnant: rapG || null, rapport_place: rapP || null });
      toast.success("Rapports PMU enregistrés !");
      setRapportFor(null);
      setRapG("");
      setRapP("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [autoAntId, setAutoAntId] = useState(null);
  const [autoAntAll, setAutoAntAll] = useState(false);

  const settleAntAuto = async (id) => {
    setAutoAntId(id);
    try {
      const res = await api.post(`/admin/courses/${id}/settle-auto`);
      toast.success(res.data.non_partant ? "Cheval non partant — prono marqué Non partant." : `Résultat récupéré : ${res.data.arrivee?.join("-")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAutoAntId(null);
    }
  };

  const settleAntAutoAll = async () => {
    setAutoAntAll(true);
    try {
      const res = await api.post("/admin/courses/settle-auto-all", null, { params: { date: form.date || undefined } });
      toast.success(`${res.data.settled}/${res.data.checked} prono(s) réglé(s) automatiquement`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAutoAntAll(false);
    }
  };

  return (
    <div>
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Create form */}
        <div className="lg:col-span-2 rounded-xl border border-[#00E676]/15 bg-[#131F17] p-6 space-y-4 h-fit">
          <h2 className="text-lg font-bold flex items-center gap-2"><Plus size={18} className="text-[#00E676]" /> Nouveau pronostic</h2>

          {mode === "geny" ? (
            <div className="space-y-4">
              <label className="block text-xs text-[#A1B5A8]">Lien Geny du cheval
                <input className={field} placeholder="https://www.geny.com/cheval/…/course/…" value={genyUrl}
                  onChange={(e) => setGenyUrl(e.target.value)} data-testid="geny-url-input" />
              </label>
              <button type="button" onClick={analyzeGeny} disabled={analyzing || !genyUrl.trim()} data-testid="geny-analyze-btn"
                className="w-full py-2.5 rounded-lg font-semibold text-[#04180a] bg-[#39FF14]/90 hover:bg-[#39FF14] disabled:opacity-50 flex items-center justify-center gap-2">
                {analyzing ? <Loader2 className="animate-spin" size={16} /> : "Analyser le lien"}
              </button>

              {genyData && (
                <div className="space-y-4 rounded-lg border border-[#00E676]/20 bg-[#0F1812] p-4" data-testid="geny-preview">
                  <div className="flex items-center gap-3">
                    {genyData.horse.casaque
                      ? <img src={genyData.horse.casaque} alt="casaque" className="w-9 h-9 object-contain shrink-0" data-testid="geny-casaque" />
                      : <span className="tabular w-9 h-9 shrink-0 grid place-items-center rounded-md bg-[#00E676] text-[#04180a] font-black">{genyData.horse.numero}</span>}
                    <div className="min-w-0">
                      <div className="font-bold truncate">{genyData.horse.numero}. {genyData.horse.cheval}</div>
                      <div className="text-xs text-[#697C6F] truncate">{genyData.horse.jockey} · Musique {genyData.horse.musique || "—"} · Cote {genyData.horse.cote ?? "—"}</div>
                    </div>
                  </div>
                  <div className="text-xs text-[#A1B5A8]">
                    {genyData.hippodrome} · {genyData.reunion} {genyData.course} · {genyData.discipline} · {genyData.heure} · {new Date(genyData.date).toLocaleDateString("fr-FR")}
                  </div>

                  <label className="block text-xs text-[#A1B5A8]">Type de pari <span className="text-[#FF3B30]">*</span>
                    <select className={field} value={genyType} onChange={(e) => setGenyType(e.target.value)} data-testid="geny-type-pari" required>
                      <option value="">— Choisir (obligatoire) —</option>
                      {TYPES_PARI.map((t) => <option key={t} value={t} className="bg-[#0F1812]">{t}</option>)}
                    </select>
                  </label>

                  <label className="block text-xs text-[#A1B5A8]">Nombre de partants
                    <input type="number" min="1" className={field} value={genyNb} onChange={(e) => setGenyNb(e.target.value)} data-testid="geny-nb-partants" />
                  </label>

                  <div className="flex items-center gap-1" data-testid="geny-stars">
                    <span className="text-xs text-[#A1B5A8] mr-1">Confiance :</span>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button type="button" key={n} onClick={() => setGenyStars(n)}>
                        <Star size={20} className={n <= genyStars ? "fill-[#39FF14] text-[#39FF14]" : "text-[#2A3B30]"} />
                      </button>
                    ))}
                  </div>

                  <label className="block text-xs text-[#A1B5A8]">Mon ticket
                    <input className={field} placeholder="Ex : Simple gagnant 5€ sur le 5" value={genyTicket} onChange={(e) => setGenyTicket(e.target.value)} data-testid="geny-mon-ticket" />
                  </label>
                  <label className="block text-xs text-[#A1B5A8]">Commentaire
                    <textarea className={field} rows={2} placeholder="Votre analyse..." value={genyComment} onChange={(e) => setGenyComment(e.target.value)} data-testid="geny-commentaire" />
                  </label>

                  <div className={`text-xs rounded-md px-3 py-2 ${(parseInt(genyNb) || 0) > 0 && (parseInt(genyNb) || 0) < 8 ? "bg-[#FFCC00]/10 text-[#FFCC00]" : "bg-[#00E676]/10 text-[#39FF14]"}`}>
                    {(parseInt(genyNb) || 0) > 0 && (parseInt(genyNb) || 0) < 8
                      ? "Moins de 8 partants : seuls les 2 premiers sont placés (le 3e est perdant)."
                      : "8 partants ou plus : les 3 premiers sont placés."}
                  </div>

                  <button type="button" onClick={publishGeny} disabled={saving} data-testid="geny-publish-btn"
                    className="w-full py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60 flex items-center justify-center gap-2">
                    {saving ? <Loader2 className="animate-spin" size={18} /> : "Publier le pronostic"}
                  </button>
                </div>
              )}
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="create-course-form">
            <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-[#A1B5A8]">Date
              <input type="date" className={field} value={form.date} onChange={set("date")} data-testid="admin-date" />
            </label>
            <label className="text-xs text-[#A1B5A8]">Heure
              <input className={field} placeholder="14h20" value={form.heure} onChange={set("heure")} />
            </label>
          </div>
          <input className={field} placeholder="Hippodrome (ex : Vincennes)" required value={form.hippodrome} onChange={set("hippodrome")} data-testid="admin-hippodrome" />
          <div className="grid grid-cols-3 gap-3">
            <input className={field} placeholder="Réunion" value={form.reunion} onChange={set("reunion")} />
            <input className={field} placeholder="Course" value={form.course} onChange={set("course")} />
            <input className={field} placeholder="Discipline" value={form.discipline} onChange={set("discipline")} />
          </div>

          <label className="block text-xs text-[#A1B5A8]">Type de pari <span className="text-[#FF3B30]">*</span>
            <select className={field} value={form.type_pari} onChange={set("type_pari")} data-testid="type-pari-select" required>
              <option value="">— Choisir (obligatoire) —</option>
              {TYPES_PARI.map((t) => <option key={t} value={t} className="bg-[#0F1812]">{t}</option>)}
            </select>
          </label>

          <label className="block text-xs text-[#A1B5A8]">Mon ticket
            <input className={field} placeholder="Ex : Simple gagnant 5€ sur le 5" value={form.mon_ticket} onChange={set("mon_ticket")} data-testid="manuel-mon-ticket" />
          </label>
          <label className="block text-xs text-[#A1B5A8]">Commentaire
            <textarea className={field} rows={2} placeholder="Votre analyse..." value={form.commentaire} onChange={set("commentaire")} data-testid="manuel-commentaire" />
          </label>

          <label className="flex items-center gap-2 cursor-pointer bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5" data-testid="moins8-label">
            <input type="checkbox" className="accent-[#39FF14] w-4 h-4" checked={form.moins_8_partants}
              onChange={(e) => setForm({ ...form, moins_8_partants: e.target.checked })} data-testid="moins8-checkbox" />
            <span className="text-sm text-[#A1B5A8]">Moins de 8 partants <span className="text-[#697C6F]">(seuls les 2 premiers sont placés, pas le 3e)</span></span>
          </label>

          <div className="space-y-3 pt-2">
            <div className="text-xs uppercase tracking-wide text-[#697C6F]">Chevaux sélectionnés</div>
            {form.selections.map((s, i) => (
              <div key={i} className="rounded-lg border border-[#00E676]/10 p-3 space-y-2 relative">
                <div className="grid grid-cols-4 gap-2">
                  <input className={field} placeholder="N°" value={s.numero} onChange={(e) => setSel(i, "numero", e.target.value)} />
                  <input className={`${field} col-span-3`} placeholder="Nom du cheval" value={s.cheval} onChange={(e) => setSel(i, "cheval", e.target.value)} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-[#A1B5A8] mr-1">Confiance :</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setSel(i, "etoiles", n)}>
                      <Star size={18} className={n <= s.etoiles ? "fill-[#39FF14] text-[#39FF14]" : "text-[#2A3B30]"} />
                    </button>
                  ))}
                </div>
                <input className={field} placeholder="Commentaire" value={s.commentaire} onChange={(e) => setSel(i, "commentaire", e.target.value)} />
                {form.selections.length > 1 && (
                  <button type="button" onClick={() => setForm({ ...form, selections: form.selections.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 bg-[#FF3B30] rounded-full p-1">
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, selections: [...form.selections, emptySel()] })} className="text-sm text-[#39FF14] flex items-center gap-1">
              <Plus size={14} /> Ajouter un cheval
            </button>
          </div>

          <button type="submit" disabled={saving} data-testid="publish-btn" className="w-full py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Publier le pronostic"}
          </button>
          </form>
          )}
        </div>

        {/* Course list */}
        <div className="lg:col-span-3 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold whitespace-nowrap">Pronostics du {new Date(form.date).toLocaleDateString("fr-FR")}</h2>
            <DateNav value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d }))} />
          </div>
          {courses.length === 0 && <p className="text-[#697C6F] text-sm">Aucun pronostic pour cette date.</p>}
          {courses.some((c) => c.programme_url && c.status !== "settled") && (
            <button onClick={settleAntAutoAll} disabled={autoAntAll} data-testid="settle-auto-all-btn" className="text-xs font-bold px-3 py-2 rounded-lg bg-[#00E676]/15 text-[#39FF14] flex items-center gap-2 disabled:opacity-50">
              {autoAntAll ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Tout régler auto (Geny)
            </button>
          )}
          {[...courses].sort((a, b) => heureToMin(a.heure) - heureToMin(b.heure)).map((c) => (
            <div key={c.id} className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5" data-testid={`admin-course-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold">
                  <span className="text-[#39FF14] text-xs mr-2">{c.reunion} {c.course}</span>
                  {c.hippodrome}
                  {c.type_pari && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/15 text-[#39FF14] font-bold uppercase">{c.type_pari}</span>}
                  {c.moins_8_partants && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#FFCC00]/15 text-[#FFCC00] font-bold">-8 PARTANTS</span>}
                  {c.status === "settled" && <span className="ml-2 text-xs text-[#00E676]">• arrivée {c.arrivee?.join("-")}</span>}
                  {c.status === "settled" && !c.non_partant && !c.rapport_gagnant && !c.rapport_place && (
                    <span data-testid={`rapports-missing-${c.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF9500]/20 text-[#FF9500] font-bold align-middle">
                      <AlertCircle size={11} /> Rapports à saisir
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {c.status !== "settled" ? (
                    <>
                      {c.programme_url && (
                        <button onClick={() => settleAntAuto(c.id)} disabled={autoAntId === c.id} data-testid={`settle-auto-${c.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#00E676]/15 text-[#39FF14] flex items-center gap-1 disabled:opacity-50">
                          {autoAntId === c.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Régler auto
                        </button>
                      )}
                      <button onClick={() => { setSettleFor(c); setArrivee(""); }} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#1A2B20] text-[#39FF14] flex items-center gap-1" data-testid={`settle-${c.id}`}>
                        <Flag size={13} /> Saisir l'arrivée
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-[#39FF14] flex items-center gap-1"><CheckCircle2 size={14} /> Terminé</span>
                  )}
                  <button
                    onClick={() => { setRapportFor(c); setRapG(c.rapport_gagnant || ""); setRapP(c.rapport_place || ""); }}
                    data-testid={`rapports-btn-${c.id}`}
                    title="Saisir les rapports PMU"
                    className={`text-xs font-bold px-3 py-1.5 rounded-md bg-[#FFD700]/15 text-[#FFD700] flex items-center gap-1 ${c.status === "settled" && !c.non_partant && !c.rapport_gagnant && !c.rapport_place ? "ring-2 ring-[#FF9500]/60" : ""}`}
                  >
                    <Award size={13} /> Rapports
                  </button>
                  <button onClick={() => remove(c.id)} className="p-1.5 rounded-md text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-2 text-sm text-[#A1B5A8]">
                {(c.selections || []).map((s) => `${s.numero} ${s.cheval}`).join(" · ")}
              </div>
            </div>
          ))}
        </div>
      </div>

      {settleFor && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" onClick={() => setSettleFor(null)}>
          <div className="w-full max-w-md rounded-xl border border-[#00E676]/30 bg-[#131F17] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Arrivée officielle</h3>
            <p className="text-sm text-[#A1B5A8] mb-4">{settleFor.hippodrome} — {settleFor.reunion} {settleFor.course}</p>
            <input
              autoFocus
              className={field}
              placeholder="Ex : 7 3 12 5  (dans l'ordre)"
              value={arrivee}
              onChange={(e) => setArrivee(e.target.value)}
              data-testid="arrivee-input"
            />
            <p className="text-xs text-[#697C6F] mt-2">Le gagnant est le 1er numéro. {settleFor.moins_8_partants ? "Course à moins de 8 partants : seuls les 2 premiers sont placés." : "Placé = top 3."} Les points sont attribués automatiquement.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div>
                <label className="text-xs font-bold text-[#FFD700] flex items-center gap-1 mb-1"><Award size={12} /> Rapport gagnant</label>
                <input className={field} placeholder="Ex : 4,20 €" value={rapG} onChange={(e) => setRapG(e.target.value)} data-testid="settle-rapport-gagnant" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#FFD700] flex items-center gap-1 mb-1"><Award size={12} /> Rapport placé</label>
                <input className={field} placeholder="Ex : 1,80 €" value={rapP} onChange={(e) => setRapP(e.target.value)} data-testid="settle-rapport-place" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setSettleFor(null); setRapG(""); setRapP(""); }} className="flex-1 py-2.5 rounded-lg bg-[#1A2B20] text-white">Annuler</button>
              <button onClick={settle} data-testid="confirm-settle" className="flex-1 py-2.5 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14]">Valider</button>
            </div>
          </div>
        </div>
      )}

      {rapportFor && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" onClick={() => setRapportFor(null)}>
          <div className="w-full max-w-md rounded-xl border border-[#FFD700]/30 bg-[#131F17] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1 flex items-center gap-2 text-[#FFD700]"><Award size={18} /> Rapports PMU</h3>
            <p className="text-sm text-[#A1B5A8] mb-4">{rapportFor.hippodrome} — {rapportFor.reunion} {rapportFor.course}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#FFD700] mb-1 block">Rapport gagnant</label>
                <input autoFocus className={field} placeholder="Ex : 4,20 €" value={rapG} onChange={(e) => setRapG(e.target.value)} data-testid="rapport-gagnant-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#FFD700] mb-1 block">Rapport placé</label>
                <input className={field} placeholder="Ex : 1,80 €" value={rapP} onChange={(e) => setRapP(e.target.value)} data-testid="rapport-place-input" />
              </div>
            </div>
            <p className="text-xs text-[#697C6F] mt-2">Ces rapports s'afficheront en or à droite de la ligne « Arrivée » sur la page Résultats.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setRapportFor(null); setRapG(""); setRapP(""); }} className="flex-1 py-2.5 rounded-lg bg-[#1A2B20] text-white">Annuler</button>
              <button onClick={saveRapports} data-testid="confirm-rapports" className="flex-1 py-2.5 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#FFD700] to-[#FFC400]">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOfficial = !!user?.is_official;
  const isMod = !!user?.is_moderator;

  const tabs = [];
  if (isAdmin || isOfficial) tabs.push({ key: "ant", label: "Pronostics", Icon: Sparkles });
  if (isAdmin) tabs.push({ key: "game", label: "Le Jeu", Icon: Ticket });
  if (isAdmin) tabs.push({ key: "gameplayers", label: "Joueurs (Jeu)", Icon: Users });
  if (isAdmin) tabs.push({ key: "banner", label: "Bandeau / Annonces", Icon: Megaphone });
  if (isAdmin) tabs.push({ key: "messages", label: "Messagerie", Icon: MessageSquare });
  if (isAdmin) tabs.push({ key: "referrals", label: "Parrainages", Icon: Share2 });
  if (isAdmin || isMod) tabs.push({ key: "mod", label: "Modération", Icon: ShieldCheck });

  const [tab, setTab] = useState(tabs[0]?.key || "ant");
  const [unreadMsg, setUnreadMsg] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    const load = () => api.get("/admin/messages/unread-count").then((r) => setUnreadMsg(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [isAdmin, tab]);

  const title = isAdmin ? "Administration" : isOfficial ? "Espace pronostiqueur" : "Espace modération";
  const subtitle = isAdmin ? "Espace expert" : isOfficial ? "Team A Nous Turf" : "Gestion du Mur";

  return (
    <div>
      <div className="mb-6">
        <div className="text-[#39FF14] font-script text-2xl">{subtitle}</div>
        <h1 className="text-4xl sm:text-5xl font-black">{title}</h1>
      </div>

      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#00E676] text-[#04180a]" : "bg-[#131F17] text-[#A1B5A8] hover:text-white"}`}>
              <t.Icon size={16} /> {t.label}
              {t.key === "messages" && unreadMsg > 0 && (
                <span data-testid="admin-tab-messages-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold grid place-items-center">
                  {unreadMsg}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {tab === "ant" ? <AntPronoAdmin /> : tab === "game" ? <AdminGame /> : tab === "gameplayers" ? <AdminGamePlayers /> : tab === "banner" ? <AdminBanner /> : tab === "messages" ? <AdminMessages /> : tab === "referrals" ? <AdminReferrals /> : <AdminModeration isAdmin={isAdmin} />}
    </div>
  );
}
